import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { LanguageContext, getTranslation } from '../LanguageContext';
import { FaCalendarAlt, FaImages, FaVideo, FaArrowLeft, FaSpinner, FaTimes, FaChevronLeft, FaChevronRight, FaSearchPlus } from 'react-icons/fa';
import './ActivityDetail.css';

const detailTranslations = {
  en: {
    loading: "Loading activity...",
    notFound: "Activity not found",
    back: "Back to Activities",
    photos: "Photos",
    videos: "Videos",
    imageOf: "Image {current} of {total}",
    noImages: "No images in this activity",
    noVideos: "No videos in this activity"
  },
  fr: {
    loading: "Chargement de l'activité...",
    notFound: "Activité non trouvée",
    back: "Retour aux activités",
    photos: "Photos",
    videos: "Vidéos",
    imageOf: "Image {current} de {total}",
    noImages: "Aucune image dans cette activité",
    noVideos: "Aucune vidéo dans cette activité"
  },
  ar: {
    loading: "جاري تحميل النشاط...",
    notFound: "النشاط غير موجود",
    back: "العودة إلى الأنشطة",
    photos: "الصور",
    videos: "الفيديوهات",
    imageOf: "صورة {current} من {total}",
    noImages: "لا توجد صور في هذا النشاط",
    noVideos: "لا توجد فيديوهات في هذا النشاط"
  }
};

const ActivityDetail = () => {
  const { id } = useParams();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(-1);
  const { language } = useContext(LanguageContext);
  const translate = (key) => getTranslation(detailTranslations, language, key);
  const isRTL = language === 'ar';

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`http://localhost:4444/activities/${id}`);
        setActivity(res.data.data);
      } catch (error) {
        console.error('Error fetching activity:', error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchActivity();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const locale = language === 'ar' ? 'ar-SA' : language === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const openImage = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
      setSelectedImage(activity.images[currentImageIndex - 1]);
    }
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (currentImageIndex < activity.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
      setSelectedImage(activity.images[currentImageIndex + 1]);
    }
  };

  if (loading) {
    return (
      <div className="activity-detail-page">
        <div className="activity-detail-loading">
          <FaSpinner className="spin" />
          <p>{translate('loading')}</p>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="activity-detail-page">
        <div className="activity-detail-loading">
          <p>{translate('notFound')}</p>
          <Link to="/activities" className="activity-detail-back">{translate('back')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`activity-detail-page ${isRTL ? 'rtl' : ''}`}>
      <div className="activity-detail-hero">
        <div className="activity-detail-hero-bg">
          {activity.images && activity.images.length > 0 && (
            <img src={activity.images[0]} alt="" />
          )}
          <div className="activity-detail-hero-overlay"></div>
        </div>
        <div className="activity-detail-hero-content">
          <Link to="/activities" className="activity-detail-back">
            <FaArrowLeft />
            <span>{translate('back')}</span>
          </Link>
          <h1>{activity.title}</h1>
          {activity.date && (
            <div className="activity-detail-date">
              <FaCalendarAlt />
              <span>{formatDate(activity.date)}</span>
            </div>
          )}
        </div>
      </div>

      {activity.description && (
        <div className="activity-detail-description">
          <p>{activity.description}</p>
        </div>
      )}

      {/* Photos Section */}
      {activity.images && activity.images.length > 0 && (
        <div className="activity-detail-section">
          <h2>
            <FaImages />
            {translate('photos')} ({activity.images.length})
          </h2>
          <div className="activity-detail-gallery">
            {activity.images.map((image, index) => (
              <div key={index} className="activity-gallery-item" onClick={() => openImage(image, index)}>
                <img src={image} alt={`${activity.title} ${index + 1}`} loading="lazy" />
                <div className="activity-gallery-overlay">
                  <FaSearchPlus />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos Section */}
      {activity.videos && activity.videos.length > 0 && (
        <div className="activity-detail-section">
          <h2>
            <FaVideo />
            {translate('videos')} ({activity.videos.length})
          </h2>
          <div className="activity-detail-videos">
            {activity.videos.map((video, index) => (
              <div key={index} className="activity-video-item">
                <ReactPlayer
                  url={video.URLVideo}
                  width="100%"
                  height="100%"
                  controls
                  light={video.URLImage || true}
                  pip
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="activity-image-modal" onClick={() => setSelectedImage(null)}>
          <button className="activity-modal-close" onClick={() => setSelectedImage(null)}>
            <FaTimes />
          </button>
          {currentImageIndex > 0 && (
            <button className="activity-modal-nav activity-modal-prev" onClick={handlePrev}>
              <FaChevronRight />
            </button>
          )}
          <div className="activity-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt="" />
            <div className="activity-modal-info">
              <span>
                {translate('imageOf')
                  .replace('{current}', currentImageIndex + 1)
                  .replace('{total}', activity.images.length)}
              </span>
            </div>
          </div>
          {currentImageIndex < activity.images.length - 1 && (
            <button className="activity-modal-nav activity-modal-next" onClick={handleNext}>
              <FaChevronLeft />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityDetail;
